import { useMemo } from 'react';
import { useScrollLock, useEscapeKey } from '../../hooks/useScrollLock';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import BasicTwoColumnData from '../Details/BasicTwoColumnData';
import { ChangeLogType } from '../../types/types';
import { HiX } from 'react-icons/hi';
import SectionHeader from '../CoreComponents/Details/SectionHeader';
import { formatChangelogDateTime } from '../../utils/exportUtils';

type ChangeLogModalProps = {
  changelogUuid: string;
  onClose: () => void;
};

export default function ChangeLogModal({ changelogUuid, onClose }: ChangeLogModalProps) {
  const { ChangeLogs } = useSelector((state: RootState) => state.ChangeLogs);
  const { ChangeLogsTypes } = useSelector((state: RootState) => state.changelogsTypes);
  const { allDisciplines } = useSelector((state: RootState) => state.Discipline);
  const { projects } = useSelector((state: RootState) => state.Project);

  // Find the specific changelog
  const changelog = useMemo(() => {
    return ChangeLogs.find(log => log.changelogUuid === changelogUuid);
  }, [ChangeLogs, changelogUuid]);

  useEscapeKey(true, onClose);
  useScrollLock(true);

  if (!changelog) {
    return null;
  }

  // Get type name
  const typeName = ChangeLogsTypes.find((type: ChangeLogType) => type.id === changelog.changelogTypeId)?.typeName || 'Unknown';

  // Get discipline name
  const discipline = allDisciplines.find(d => String(d.disciplineId) === String(changelog.changedByDisciplineId));
  const disciplineName = discipline?.disciplineName || changelog.changedByDisciplineId;

  // Get project text
  const project = projects.find(p => String(p.projectNumber) === String(changelog.projectNumber));
  const projectText = project ? `${project.projectNumber} - ${project.projectName}` : changelog.projectNumber;

  // Prepare data for BasicTwoColumnData component
  const changelogData = [
    { label: 'Type', value: typeName },
    { label: 'Description', value: changelog.description || 'N/A' },
    { label: 'Changed By', value: changelog.changedBy },
    { label: 'Email', value: changelog.changedEmail },
    { label: 'Changed On', value: formatChangelogDateTime(changelog.changedOn) },
    { label: 'Project', value: projectText },
    { label: 'Discipline', value: disciplineName },
    { label: 'Punchlist UUID', value: changelog.punchlistUuid || 'N/A' },
    { label: 'CT UUID', value: changelog.ctUuid || 'N/A' },
    { label: 'Changelog UUID', value: changelog.changelogUuid },
  ];

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4 flex justify-between items-center">
          <SectionHeader title="Changelog Details" />
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-orange-1000 transition-colors"
            title="Close (Esc)"
          >
            <HiX size={24} />
          </button>
        </div>

        <div className="p-6">
          <BasicTwoColumnData
            data={changelogData}
            labelClassName="font-semibold text-gray-700 dark:text-gray-300 min-w-[150px]"
            rowClassName="py-3 border-b border-gray-200 dark:border-gray-700 last:border-b-0"
          />
        </div>
      </div>
    </div>
  );
}
